import React, { useState } from 'react';
import PropTypes from 'prop-types';
import priceIcon from '../../assets/prices.svg';
import css from './EventCard.module.css';

/**
 * Shared EventCard – Crew-inspired dimensions. Used on home, Crew pages, price insights.
 * Handles up to 3 lines of event details gracefully. Same size and structure everywhere.
 */
const EventCard = ({
  event,
  onClick,
  showCrewInfo = false,
  actionLabel = undefined,
  onActionClick = undefined,
  showPriceInsightsAction = false,
  variant = 'default',
  theme = 'light',
}) => {
  const [imageReady, setImageReady] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);
  const isClickable = !!onClick;
  const isLandingVariant = variant === 'landing';
  const isDark = theme === 'dark';
  const handleCardClick = e => {
    if (!isClickable) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    if (onClick) onClick(event);
  };

  const detailsText = [event.date, event.time, event.venue].filter(Boolean).join(' • ') || '—';
  const landingDateText = [event.date, event.time].filter(Boolean).join(' • ');
  const landingLocationText =
    event.locationLabel || event.location || event.venue || 'Location TBD';
  const landingPriceText = event.price ? event.price.replace(/^From\s*/i, '') : null;
  const handleImageLoad = () => {
    setImageReady(true);
  };

  const handleImageError = () => {
    setImageFailed(true);
    setImageReady(false);
  };

  return (
    <div
      className={`${css.eventCard} ${isLandingVariant ? css.eventCardLanding : ''} ${
        isDark ? css.eventCardDark : ''
      } ${isClickable ? css.eventCardClickable : ''}`}
      onClick={handleCardClick}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={
        isClickable
          ? e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick(event);
              }
            }
          : undefined
      }
    >
      <div
        className={css.eventImageContainer}
        style={{ ['--event-color']: event.color || '#6366f1' }}
      >
        <div className={css.eventImagePlaceholder} aria-hidden="true" />
        {event.image ? (
          <>
            <img
              src={event.image}
              alt={event.title}
              className={`${css.eventImage} ${imageReady && !imageFailed ? css.imageReady : ''}`}
              loading="lazy"
              decoding="async"
              onLoad={handleImageLoad}
              onError={handleImageError}
            />
            <div
              className={`${css.eventImageBg} ${imageFailed ? css.imageFallbackVisible : ''}`}
              style={{ backgroundColor: event.color || '#6366f1' }}
              aria-hidden="true"
            />
          </>
        ) : (
          <div
            className={css.eventImageBg}
            style={{
              backgroundColor: event.color || '#6366f1',
              backgroundImage: 'none',
            }}
            aria-hidden="true"
          />
        )}
      </div>
      <div className={css.eventInfo}>
        {isLandingVariant && landingDateText ? (
          <p className={css.eventMeta}>{landingDateText}</p>
        ) : null}
        <h3 className={css.eventTitle}>{event.title}</h3>
        <p className={isLandingVariant ? css.eventLocation : css.eventDetails}>
          {isLandingVariant ? landingLocationText : detailsText}
        </p>
        {showCrewInfo && event.crew && (
          <div className={css.crewInfo}>
            <div className={css.crewAvatar} style={{ backgroundColor: event.color || '#7B2CBF' }}>
              {event.crew.charAt(0)}
            </div>
            <span className={css.crewName}>{event.crew}</span>
            {event.members != null && (
              <span className={css.memberCount}>{event.members} members</span>
            )}
          </div>
        )}
        {actionLabel && (
          <div className={css.eventAction}>
            <button
              type="button"
              className={css.eventActionButton}
              onClick={e => {
                e.stopPropagation();
                if (onActionClick) onActionClick(event);
              }}
            >
              {actionLabel}
            </button>
          </div>
        )}
        {isLandingVariant && (
          <div className={css.eventLandingFooter}>
            <span className={css.eventPrice}>
              {!showPriceInsightsAction && landingPriceText ? (
                <>
                  From <strong>{landingPriceText}</strong>
                </>
              ) : (
                <img src={priceIcon} alt="" className={css.eventPriceIcon} aria-hidden="true" />
              )}
            </span>
            <span className={css.eventTicketsLink}>
              {showPriceInsightsAction || !landingPriceText ? 'See prices' : 'Get tickets'} &rarr;
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

EventCard.propTypes = {
  event: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string.isRequired,
    venue: PropTypes.string,
    location: PropTypes.string,
    locationLabel: PropTypes.string,
    date: PropTypes.string,
    time: PropTypes.string,
    price: PropTypes.string,
    category: PropTypes.string,
    crew: PropTypes.string,
    color: PropTypes.string,
    members: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    image: PropTypes.string,
    matchReasons: PropTypes.arrayOf(PropTypes.string),
    score: PropTypes.number,
    ticketmasterUrl: PropTypes.string,
  }).isRequired,
  onClick: PropTypes.func,
  showCrewInfo: PropTypes.bool,
  actionLabel: PropTypes.string,
  onActionClick: PropTypes.func,
  showPriceInsightsAction: PropTypes.bool,
  variant: PropTypes.string,
  theme: PropTypes.oneOf(['light', 'dark']),
};

export default EventCard;
